"use client";

import * as React from "react";
import { AlertTriangle, X } from "lucide-react";

/**
 * One-day maintenance notice shown at the very top of every page (sign-in and
 * dashboard alike — it is mounted in app/layout.tsx).
 *
 * Self-expiring: it stops rendering after MAINTENANCE_END with no follow-up
 * deploy, so it truly shows "just today". Dismissible per browser session.
 *
 * To change/extend the window, edit MAINTENANCE_END. To remove entirely, delete
 * this component and its usage in app/layout.tsx.
 */

// End of Aug 25 2026, US Central (UTC-5) == Aug 26 2026 05:00 UTC. After this
// instant the banner never renders again.
const MAINTENANCE_END = new Date("2026-08-26T05:00:00Z").getTime();

// Bump the date suffix if you reuse this for a future maintenance window so a
// prior dismissal doesn't hide the new notice.
const DISMISS_KEY = "maintenance_banner_dismissed_2026-08-25";

export function MaintenanceBanner() {
  // Start hidden so server render and first client render match (no hydration
  // mismatch); reveal on mount once we can read the clock + sessionStorage.
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    if (Date.now() >= MAINTENANCE_END) return;
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === "1") return;
    } catch {
      // sessionStorage unavailable (private mode / SSR) — still show the notice.
    }
    setVisible(true);
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // ignore — worst case the banner reappears on next navigation
    }
    setVisible(false);
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="sticky top-0 z-[60] flex items-center justify-center gap-2 bg-amber-500 px-8 py-1.5 text-center text-xs font-medium text-amber-950 shadow sm:text-sm"
    >
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>
        Scheduled maintenance today — the site may be briefly unavailable.
        Thanks for your patience.
      </span>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss maintenance notice"
        className="absolute right-1.5 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded transition hover:bg-amber-950/10"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
